import { FC } from 'react';
import { InvestorProfile } from '@/types/index';

interface Props {
  profile: Partial<InvestorProfile>;
  onChange: (data: Partial<InvestorProfile>) => void;
}

interface FieldProps {
  label: string;
  unit?: string;
  hint?: string;
  value: number | null | undefined;
  onChange: (val: number | null) => void;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
}

const NumField: FC<FieldProps> = ({ label, unit, hint, value, onChange, step = 0.5, min = 0, max, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label} {unit && <span className="text-gray-400">({unit})</span>}
    </label>
    <input
      type="number"
      step={step}
      min={min}
      max={max}
      value={value ?? ''}
      onChange={e => onChange(e.target.value ? parseFloat(e.target.value) : null)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const CANTONS_CH = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR',
  'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG',
  'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH',
];

export const Step5CriteresFinanciers: FC<Props> = ({ profile, onChange }) => {
  const loc = (profile.localisation as { cantons?: string[]; villes?: string[] } | null) ?? {};
  const cantons = loc.cantons ?? [];

  const toggleCanton = (c: string) => {
    const updated = cantons.includes(c) ? cantons.filter(x => x !== c) : [...cantons, c];
    onChange({ localisation: { ...loc, cantons: updated } });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Cantons sélectionnés</h3>
        <div className="flex flex-wrap gap-1.5">
          {CANTONS_CH.map(c => (
            <button
              key={c}
              onClick={() => toggleCanton(c)}
              className={`px-2.5 py-1 rounded-md border text-xs font-semibold transition-all
                ${cantons.includes(c)
                  ? 'border-blue-500 bg-blue-500 text-white'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-300'}`}
            >
              {c}
            </button>
          ))}
        </div>
        {cantons.length === 0
          ? <p className="text-xs text-gray-400 mt-2">Aucun canton sélectionné = toute la Suisse acceptée</p>
          : <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">Les annonces hors de ces cantons seront automatiquement rejetées à l'import</p>
        }
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Seuils personnels</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <NumField
            label="Cash flow min (CHF/mois)"
            hint="Cash flow positif minimum attendu"
            value={profile.cashflowMin}
            onChange={v => onChange({ cashflowMin: v })}
            step={100}
            placeholder="ex: 200"
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Filtres de rejet automatique</h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
          Les annonces importées ne respectant pas ces seuils sont automatiquement classées "Rejetées". Laisser vide pour désactiver un filtre. Les cantons sélectionnés ci-dessus font également partie de ces filtres.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <NumField
            label="Rendement brut minimum (%)"
            hint="Rejette si rendement estimé < seuil"
            value={profile.rejectionMinYield}
            onChange={v => onChange({ rejectionMinYield: v })}
            step={0.1}
            placeholder="Désactivé"
          />
          <NumField
            label="Prix maximum (CHF)"
            hint="Rejette si prix demandé > seuil"
            value={profile.rejectionMaxPrice}
            onChange={v => onChange({ rejectionMaxPrice: v })}
            step={10000}
            placeholder="Désactivé"
          />
          <NumField
            label="Surface minimale (m²)"
            hint="Rejette si surface < seuil"
            value={profile.rejectionMinArea}
            onChange={v => onChange({ rejectionMinArea: v })}
            step={5}
            placeholder="Désactivé"
          />
          <NumField
            label="Pièces minimum"
            hint="Rejette si nombre de pièces < seuil"
            value={profile.rejectionMinRooms}
            onChange={v => onChange({ rejectionMinRooms: v })}
            step={0.5}
            placeholder="Désactivé"
          />
          <NumField
            label="Écart max vs marché (%)"
            hint="Rejette si prix > estimation marché × (1 + seuil/100)"
            value={profile.rejectionMaxPriceVsMarket}
            onChange={v => onChange({ rejectionMaxPriceVsMarket: v })}
            step={1}
            placeholder="Désactivé"
          />
        </div>
      </div>

    </div>
  );
};
